import React, { useState, useCallback, useRef, forwardRef, useImperativeHandle } from "react";
import {
    Box,
    Card,
    CardContent,
    Grid,
    TextField,
    Button,
    Typography,
    Collapse,
    CircularProgress,
    Skeleton,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Divider,
    InputAdornment,
    Tooltip,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import KeyIcon from "@mui/icons-material/Key";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useLanguage } from "../../../context/LanguageContext";
import { StoreAutocomplete } from "../../../components/Common";
import { CustomerSearchParams } from "../../../types/Customer";

// ─── Types ───────────────────────────────────────────────────────────────────

interface SearchFormProps {
    onSearch: (params: CustomerSearchParams) => void;
    onReset: () => void;
    isLoading: boolean;
    identifierTypes: any[];
    personAttributes: any[];
    refLoading: boolean;
    wayTypes?: any[];
    countries?: any[];
    stores: any[] | null;
}

export interface SearchFormHandle {
    reset: () => void;
}

interface CheckboxGroupProps {
    items: any[];
    selected: string[];
    allLabel: string;
    getId: (item: any) => string;
    getLabel: (item: any) => string;
    onChange: (value: string, checked: boolean) => void;
}

// ─── Sous-composant mémoïsé pour les groupes de checkboxes ───────────────────
// Un clic sur "identifiers" ne re-rend pas "personAttributes" et vice-versa.

const CheckboxGroup = React.memo(({
    items,
    selected,
    allLabel,
    getId,
    getLabel,
    onChange,
}: CheckboxGroupProps) => (
    <FormGroup row sx={{ gap: 0.5 }}>
        <FormControlLabel
            control={
                <Checkbox
                    size="small"
                    checked={selected.includes("0")}
                    onChange={(e) => onChange("0", e.target.checked)}
                />
            }
            label={allLabel}
        />
        {items.map((item) => {
            const id = getId(item);
            const label = getLabel(item);
            return (
                <FormControlLabel
                    key={id}
                    control={
                        <Checkbox
                            size="small"
                            checked={selected.includes(id)}
                            onChange={(e) => onChange(id, e.target.checked)}
                        />
                    }
                    label={label}
                />
            );
        })}
    </FormGroup>
));

// ─── Helper ──────────────────────────────────────────────────────────────────

function toggleMutex(current: string[], value: string, checked: boolean): string[] {
    if (value === "0") return ["0"];
    if (checked) return [...current.filter((v) => v !== "0"), value];
    const next = current.filter((v) => v !== value);
    return next.length === 0 ? ["0"] : next;
}

// ─── Composant principal ──────────────────────────────────────────────────────

const SearchForm = forwardRef<SearchFormHandle, SearchFormProps>(({
    onSearch,
    onReset,
    isLoading,
    identifierTypes,
    personAttributes,
    refLoading,
    wayTypes,
    countries,
    stores,
}: SearchFormProps, ref): React.ReactElement => {
    const { t } = useLanguage();

    // ── État minimal : uniquement les champs qui nécessitent un re-render visuel ──
    // Les TextField textuels sont NON-CONTRÔLÉS → zéro re-render React à la frappe.
    const [identifiers, setIdentifiers] = useState<string[]>(["0"]);
    const [personAttrs, setPersonAttrs] = useState<string[]>(["0"]);
    const [address3Type, setAddress3Type] = useState("");
    const [country, setCountry] = useState("");
    const [entityId, setEntityId] = useState("");
    const [showAdvanced, setShowAdvanced] = useState(false);
    // resetKey force le re-mount du <form> pour vider les inputs non-contrôlés
    const [resetKey, setResetKey] = useState(0);

    const formRef = useRef<HTMLFormElement>(null);

    // ── Submit : lit les valeurs texte via FormData, sans aucun état React ────
    const handleSearch = useCallback(() => {
        const fd = formRef.current ? new FormData(formRef.current) : new FormData();
        const str = (name: string) => (fd.get(name) as string) ?? "";
        onSearch({
            lastname:                  str("lastname"),
            firstname:                 str("firstname"),
            identifier:                str("identifier"),
            webIdentifier:             str("webIdentifier"),
            personId:                  str("personId"),
            identifiers,
            personAttributes:          personAttrs,
            birthdate:                 str("birthdate"),
            email:                     str("email"),
            cellphone:                 str("cellphone"),
            phone:                     str("phone"),
            address1:                  str("address1"),
            address2:                  str("address2"),
            address3Num:               str("address3Num"),
            address3Type,
            address3Name:              str("address3Name"),
            address4:                  str("address4"),
            zipcode:                   str("zipcode"),
            city:                      str("city"),
            country,
            transactionReceiptNumber:  str("transactionReceiptNumber"),
            transactionItemReference:  str("transactionItemReference"),
            transactionAmountMin:      str("transactionAmountMin"),
            transactionAmountMax:      str("transactionAmountMax"),
            transactionDate:           str("transactionDate"),
            transactionHour:           str("transactionHour"),
            entityId,
        });
    }, [onSearch, identifiers, personAttrs, address3Type, country, entityId]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => { if (e.key === "Enter") handleSearch(); },
        [handleSearch]
    );

    const handleReset = useCallback(() => {
        setIdentifiers(["0"]);
        setPersonAttrs(["0"]);
        setAddress3Type("");
        setCountry("");
        setEntityId("");
        setResetKey((k) => k + 1); // vide les inputs non-contrôlés
        onReset();
    }, [onReset]);

    useImperativeHandle(ref, () => ({ reset: handleReset }), [handleReset]);

    const handleIdentifiersChange = useCallback((value: string, checked: boolean) => {
        setIdentifiers((prev) => toggleMutex(prev, value, checked));
    }, []);

    const handlePersonAttrsChange = useCallback((value: string, checked: boolean) => {
        setPersonAttrs((prev) => toggleMutex(prev, value, checked));
    }, []);

    const handleAddress3TypeChange = useCallback(
        (e: SelectChangeEvent<string>) => setAddress3Type(e.target.value), []
    );
    const handleCountryChange = useCallback(
        (e: SelectChangeEvent<string>) => setCountry(e.target.value), []
    );
    const handleEntityIdChange = useCallback((id: string) => setEntityId(id), []);
    const handleToggleAdvanced = useCallback(() => setShowAdvanced((v) => !v), []);

    const sectionTitle = (label: string) => (
        <Typography variant="subtitle2" sx={{ color: "primary.main", fontWeight: 600, mb: 1.5, mt: 2 }}>
            {label}
        </Typography>
    );

    return (
        <Card sx={{ mb: 2 }}>
            {/* onKeyDown sur le <form> natif — pas sur CardContent */}
            <CardContent>
                <form ref={formRef} onKeyDown={handleKeyDown} onSubmit={(e) => { e.preventDefault(); handleSearch(); }}>
                    {/* ── resetKey provoque le re-mount du contenu non-contrôlé ── */}
                    <Box key={resetKey}>

                        {/* === Champs principaux — ligne 1 === */}
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                                <TextField fullWidth size="small"
                                    label={t("pages.customers.searchForm.lastname")}
                                    name="lastname" defaultValue=""
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Tooltip title={t("pages.customers.searchForm.nameMatchHint")} arrow placement="top">
                                                    <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.disabled", cursor: "help" }} />
                                                </Tooltip>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                                <TextField fullWidth size="small"
                                    label={t("pages.customers.searchForm.firstname")}
                                    name="firstname" defaultValue=""
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Tooltip title={t("pages.customers.searchForm.nameMatchHint")} arrow placement="top">
                                                    <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.disabled", cursor: "help" }} />
                                                </Tooltip>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <TextField fullWidth size="small" autoFocus
                                    label={t("pages.customers.searchForm.identifier")}
                                    name="identifier" defaultValue=""
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Tooltip title={t("pages.customers.searchForm.nameMatchHint")} arrow placement="top">
                                                    <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.disabled", cursor: "help", mr: 0.5 }} />
                                                </Tooltip>
                                                <KeyIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                                <TextField fullWidth size="small"
                                    label={t("pages.customers.searchForm.webIdentifier")}
                                    name="webIdentifier" defaultValue=""
                                />
                            </Grid>
                            {/* PersonId : inline sur md+, pleine largeur (ligne suivante) sur sm */}
                            <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                                <TextField fullWidth size="small"
                                    label={t("pages.customers.searchForm.personId")}
                                    name="personId" defaultValue=""
                                    inputProps={{ style: { textTransform: 'uppercase' } }}
                                />
                            </Grid>
                        </Grid>

                        {/* === Champs principaux — ligne 2 === */}
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            {/* Même largeur md que "Nom" (md:2) */}
                            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                                <TextField fullWidth size="small"
                                    label={t("pages.customers.searchForm.cellphone")}
                                    name="cellphone" defaultValue=""
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                                <TextField fullWidth size="small"
                                    label={t("pages.customers.searchForm.email")}
                                    type="email" name="email" defaultValue=""
                                />
                            </Grid>
                        </Grid>

                        {/* === Mode avancé === */}
                        <Collapse in={showAdvanced}>
                            <Divider sx={{ mt: 2, mb: 1 }} />

                            {sectionTitle(t("pages.customers.searchForm.byCoordinates"))}
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <TextField fullWidth size="small"
                                        label={t("pages.customers.searchForm.birthdate")}
                                        type="date" name="birthdate" defaultValue=""
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{ onClick: (e: React.MouseEvent<HTMLInputElement>) => { (e.target as HTMLInputElement).showPicker?.(); } }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <TextField fullWidth size="small"
                                        label={t("pages.customers.searchForm.phone")}
                                        name="phone" defaultValue=""
                                    />
                                </Grid>
                            </Grid>

                            {sectionTitle(t("pages.customers.searchForm.byAddress"))}
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <TextField fullWidth size="small"
                                        label={t("pages.customers.searchForm.apartment")}
                                        name="address1" defaultValue=""
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <TextField fullWidth size="small"
                                        label={t("pages.customers.searchForm.entrance")}
                                        name="address2" defaultValue=""
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                                    <TextField fullWidth size="small"
                                        label={t("pages.customers.searchForm.streetNumber")}
                                        name="address3Num" defaultValue=""
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>{t("pages.customers.searchForm.streetType")}</InputLabel>
                                        <Select
                                            value={address3Type}
                                            label={t("pages.customers.searchForm.streetType")}
                                            onChange={handleAddress3TypeChange}
                                            MenuProps={{ sx: { height: "300px" } }}
                                        >
                                            <MenuItem value=""><em>—</em></MenuItem>
                                            {wayTypes?.map((wt) => (
                                                <MenuItem key={String(wt.id)} value={String(wt.id)}>{wt.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                                    <TextField fullWidth size="small"
                                        label={t("pages.customers.searchForm.streetName")}
                                        name="address3Name" defaultValue=""
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <TextField fullWidth size="small"
                                        label={t("pages.customers.searchForm.locality")}
                                        name="address4" defaultValue=""
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                                    <TextField fullWidth size="small"
                                        label={t("pages.customers.searchForm.zipCode")}
                                        name="zipcode" defaultValue=""
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <TextField fullWidth size="small"
                                        label={t("pages.customers.searchForm.city")}
                                        name="city" defaultValue=""
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>{t("pages.customers.searchForm.country")}</InputLabel>
                                        <Select
                                            value={country}
                                            label={t("pages.customers.searchForm.country")}
                                            onChange={handleCountryChange}
                                            MenuProps={{ sx: { height: "300px" } }}
                                        >
                                            <MenuItem value=""><em>—</em></MenuItem>
                                            {countries?.map((c) => (
                                                <MenuItem key={String(c.id || c.code)} value={String(c.name)}>
                                                    {c.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            {sectionTitle(t("pages.customers.searchForm.byPurchase"))}
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <TextField fullWidth size="small"
                                        label={t("pages.customers.searchForm.ticket")}
                                        name="transactionReceiptNumber" defaultValue=""
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <TextField fullWidth size="small"
                                        label={t("pages.customers.searchForm.articleCode")}
                                        name="transactionItemReference" defaultValue=""
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <TextField
                                            sx={{ flex: 1, minWidth: 0 }}
                                            size="small"
                                            label={t("pages.customers.searchForm.amountBetween")}
                                            type="number" name="transactionAmountMin" defaultValue=""
                                        />
                                        <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                                            {t("pages.customers.searchForm.and")}
                                        </Typography>
                                        <TextField
                                            sx={{ flex: 1, minWidth: 0 }}
                                            size="small"
                                            label="max"
                                            type="number" name="transactionAmountMax" defaultValue=""
                                        />
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <TextField fullWidth size="small"
                                        label={t("pages.customers.searchForm.purchaseDate")}
                                        type="date" name="transactionDate" defaultValue=""
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{ onClick: (e: React.MouseEvent<HTMLInputElement>) => { (e.target as HTMLInputElement).showPicker?.(); } }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                                    <TextField fullWidth size="small"
                                        label={t("pages.customers.searchForm.purchaseTime")}
                                        type="time" name="transactionHour" defaultValue=""
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <StoreAutocomplete
                                        stores={stores ?? []}
                                        value={entityId}
                                        onChange={handleEntityIdChange}
                                        label={t("pages.customers.searchForm.store")}
                                    />
                                </Grid>
                            </Grid>
                        </Collapse>

                        {/* === Boutons === */}
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2.5, flexWrap: "wrap", alignItems: "center" }}>
                            <Button
                                variant="text"
                                endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                onClick={handleToggleAdvanced}
                                size="small"
                                sx={{ mr: "auto" }}
                            >
                                {showAdvanced
                                    ? t("pages.customers.searchForm.hideAdvanced")
                                    : t("pages.customers.searchForm.advancedSearch")}
                            </Button>
                            <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={handleReset} disabled={isLoading}>
                                {t("pages.customers.searchForm.reset")}
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                                disabled={isLoading}
                            >
                                {t("pages.customers.searchForm.search")}
                            </Button>
                        </Box>

                    </Box>
                </form>
            </CardContent>
        </Card>
    );
});

export default SearchForm;
